import { useRef, useState } from "react";
import { api } from "../api";
import { Card, Btn, ConfirmDialog } from "./Shared";

/**
 * Taking a copy of the database off the server.
 *
 * The confirmation is not ceremony. The file is plain SQL and is not encrypted,
 * so wherever it lands it is readable — and while it deliberately carries no
 * credentials, it does carry every project, customer, price and log entry the
 * scope covers. Somebody should choose that on purpose, and the log records that
 * they did.
 */
export default function BackupCard({ scope, groupId, groupName }) {
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState(null);
  const whole = scope === "full";
  const url = whole ? "/api/admin/backup.sql" : `/api/groups/${groupId}/backup.sql`;

  // Importing one back. **Adds what is missing and never overwrites**, so the
  // dangerous-sounding half of "restore" is not on offer here at all — which is
  // why this sits in the same card as the export rather than behind a warning of
  // its own.
  const importUrl = whole ? "/api/admin/import" : `/api/groups/${groupId}/import`;
  const fileRef = useRef(null);
  const [chosen, setChosen] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  // The dry run is the default and it is the point: the report says what WOULD
  // be added before anything is. `run` is only ever true from the button that
  // appears after a dry run has been read.
  const send = async (file, dryRun) => {
    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const out = await api.upload(`${importUrl}?dry_run=${dryRun ? "true" : "false"}`, form);
      setReport(out);
      if (!dryRun) setChosen(null);
    } catch (err) {
      setError(err.message || "The file could not be imported.");
      setReport(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <h3 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700 }}>
        {whole ? "Backup" : "Backup this group"}
      </h3>
      <p style={{ margin: "0 0 16px", fontSize: 13, color: "#6b7280" }}>
        {whole
          ? "The whole database as SQL, restorable with psql."
          : `Everything recorded for ${groupName || "this group"} — its projects, quotations, documents and log — as SQL.`}
        {" "}
        <strong>Passwords and storage credentials are not included</strong>, so a restored portal
        starts with neither and both have to be set again. That is what makes the file safe to
        keep somewhere ordinary.
      </p>

      <Btn onClick={() => setConfirm(true)}>Download .sql</Btn>

      <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid #e5e7eb" }}>
        <h3 style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 700 }}>Import a backup</h3>
        <p style={{ margin: "0 0 12px", fontSize: 13, color: "#6b7280" }}>
          Adds anything in the file that is not here already. <strong>Nothing is replaced and
          nothing is deleted</strong> — where a row already exists, the one here wins. The file is
          checked before anything is written, and you see what would change first.
        </p>

        <input
          ref={fileRef}
          type="file"
          accept=".sql,text/plain"
          data-testid="import-file"
          onChange={e => {
            const file = e.target.files?.[0] || null;
            setChosen(file);
            setReport(null);
            setError(null);
            if (file) send(file, true);
          }}
          style={{ fontSize: 13 }}
        />

        {busy && <p style={{ margin: "10px 0 0", fontSize: 13, color: "#6b7280" }}>Reading…</p>}

        {report && (
          <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 8,
                        background: report.dry_run ? "#f3f4f6" : "#ecfdf5",
                        border: "1px solid #d1d5db", fontSize: 13 }}>
            <p style={{ margin: "0 0 8px", fontWeight: 600 }}>
              {report.dry_run
                ? `Nothing has been written yet. This file would add ${report.totals.added} row(s).`
                : `Imported: ${report.totals.added} row(s) added.`}
            </p>
            {/* Three outcomes, not two. "Skipped" is the duplicate rule working
                and is the expected number on a re-import; "could not place" is
                the one worth reading. */}
            <ul style={{ margin: 0, paddingLeft: 18, color: "#374151" }}>
              <li>{report.totals.skipped} already here, left untouched</li>
              <li>{report.totals.ignored} in tables this portal never imports</li>
              <li>{report.totals.could_not_place} could not be placed</li>
              {/* A row that did come in but lost something on the way, such as a
                  group whose quotation prefix another group already holds. */}
              {report.totals.needs_attention > 0 && (
                <li>{report.totals.needs_attention} imported but needing attention</li>
              )}
            </ul>
            {report.totals.could_not_place > 0 && (
              <div style={{ marginTop: 8, color: "#b45309" }}>
                {Object.entries(report.could_not_place).map(([table, whys]) => (
                  <div key={table}>
                    <strong>{table}</strong>: {whys.join("; ")}
                  </div>
                ))}
              </div>
            )}
            {report.totals.needs_attention > 0 && (
              <div style={{ marginTop: 8, color: "#b45309" }}>
                {Object.entries(report.needs_attention || {}).map(([table, whys]) => (
                  <div key={table}>
                    <strong>{table}</strong>: {whys.join("; ")}
                  </div>
                ))}
              </div>
            )}
            {report.dry_run && report.totals.added > 0 && (
              <Btn style={{ marginTop: 10 }} disabled={busy}
                   onClick={() => chosen && send(chosen, false)}>
                Import these {report.totals.added} row(s)
              </Btn>
            )}
          </div>
        )}
      </div>

      {error && (
        <div role="alert" style={{ marginTop: 12, padding: "10px 12px", borderRadius: 8,
                                   background: "#fef2f2", color: "#b91c1c", fontSize: 13 }}>
          {error}
        </div>
      )}

      {confirm && (
        <ConfirmDialog
          title="Download a copy of the database?"
          confirmLabel="Download"
          tone="primary"
          onConfirm={async () => {
            // Fetched with the session token, not navigated to: a navigation
            // reaches an admin-only endpoint with no Authorization header and
            // saves the refusal as a .sql file.
            //
            // Awaited, and the rejection shown. Unhandled, a 403 or a dump that
            // failed to build closed this dialog and did nothing else — leaving
            // someone believing they had a backup, which is the one belief this
            // button exists to make true.
            setError(null);
            setConfirm(false);
            try {
              await api.saveFile(url);
            } catch (err) {
              setError(err.message || "The backup could not be downloaded.");
            }
          }}
          onClose={() => setConfirm(false)}
        >
          <div style={{ fontSize: 14, color: "#374151", lineHeight: 1.6 }}>
            <p style={{ margin: "0 0 12px" }}>
              The file is <strong>not encrypted</strong>. It contains no passwords and no storage
              keys, but it does contain every project, customer, price and log entry
              {whole ? " in the portal" : " in this group"}.
            </p>
            <p style={{ margin: "0 0 12px" }}>
              This download is recorded in the log with your name.
            </p>
            <p style={{ margin: 0, fontSize: 13, color: "#6b7280" }}>
              The last line of the file is its own MD5. To check it arrived whole:{" "}
              <code style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                head -n -1 file.sql | md5sum
              </code>
            </p>
          </div>
        </ConfirmDialog>
      )}
    </Card>
  );
}
